/**
 * TemplateTemplate — CRUD da entidade Template (perfil de testes do LinkInsight).
 *
 * Migrado do conceito de `MonitoramentoTemplate` do simplerp (Fase 3.1) — define quais testes
 * rodam (banda/ping/status/stream/ip/página) e como os resultados são exibidos.
 *
 * Formulário em abas (Principal/Adicionais, mesmo padrão de hay-hotspot/PlanoTemplate.js):
 * "Principal" reúne o que o template efetivamente testa; "Adicionais" reúne identidade visual
 * da página pública (nome/imagem/tema) e comportamento/mensagens secundárias.
 */
import React, { Component } from 'react';
import { TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';

import { TabViewComErro } from 'hay-core-react';
import { EntityGrid } from 'hay-core-react';
import { ModelTextInput } from 'hay-core-react';
import { ModelTextArea } from 'hay-core-react';
import { ModelDropDown } from 'hay-core-react';
import { ModelBoolean } from 'hay-core-react';
import { ModelFile } from 'hay-core-react';
import { UtilService } from 'hay-core-react';
import Template from '../../../models/Template';
import { buildRelacionados } from '../../../service/RotasService';
import TestebandaEnum from '../../../enums/TemplateTestebandaEnum';
import TestestatusEnum from '../../../enums/TemplateTestestatusEnum';
import TestequalidadestreamEnum from '../../../enums/TemplateTestequalidadestreamEnum';
import TesteipEnum from '../../../enums/TemplateTesteipEnum';
import ExibicaoresultadoEnum from '../../../enums/TemplateExibicaoresultadoEnum';
import TemaEnum from '../../../enums/TemplateTemaEnum';
import TemaOptionLabel from '../../../components/ui/TemaOptionLabel';

// Prompt sugerido pro botão "Usar sugestão" (ver TemplateFormulario) — não é `defaultsTo` do
// model de propósito: preencher automaticamente ativaria a Avaliação por IA em todo Template
// novo, sem o admin decidir isso explicitamente.
const SUGESTAO_PROMPT_AVALIACAOIA = `Você é um especialista em redes e telecomunicações, atuando para uma empresa provedora de internet. Com base nos dados técnicos coletados abaixo (download/upload, ping, jitter, qualidade de stream, abertura de páginas, entre outros), escreva uma análise CURTA e objetiva da qualidade da conexão deste cliente — no máximo 100 palavras.

Cubra, em frases diretas (sem subtítulos nem listas longas): um resumo geral da experiência esperada, o principal ponto forte ou problema identificado, e uma sugestão prática, só se houver algo relevante a melhorar.

Evite jargão técnico — o texto será lido diretamente pelo cliente final, sem conhecimento técnico. Seja cordial e direto.`;

const model = {
  descricao: { exibir: 'main.template.descricao', visivel: true, validacao: { obrigatorio: true } },
  testebanda: { exibir: 'main.template.testebanda', visivel: true, tipo: 'enum', entidade: 'template.testebanda' },
  testarping: { exibir: 'main.template.testarping' },
  testestatus: { exibir: 'main.template.testestatus', tipo: 'enum', entidade: 'template.testestatus' },
  testequalidadestream: { exibir: 'main.template.testequalidadestream', tipo: 'enum', entidade: 'template.testequalidadestream' },
  testeip: { exibir: 'main.template.testeip', tipo: 'enum', entidade: 'template.testeip' },
  testepageopen: { exibir: 'main.template.testepageopen', tipo: 'boolean' },
  testepageresponse: { exibir: 'main.template.testepageresponse', tipo: 'boolean' },
  exibicaoresultado: { exibir: 'main.template.exibicaoresultado', tipo: 'enum', entidade: 'template.exibicaoresultado' },
  identificadorterceiroscampo: { exibir: 'main.template.identificadorterceiroscampo' },
  exibirresultados: { exibir: 'main.template.exibirresultados', tipo: 'boolean' },
  exibirclassificacao: { exibir: 'main.template.exibirclassificacao', tipo: 'boolean' },
  permitirreinicio: { exibir: 'main.template.permitirreinicio', tipo: 'boolean' },
  blocospermitidos: { exibir: 'main.template.blocospermitidos' },
  mensagemblocosnaopermitidos: { exibir: 'main.template.mensagemblocosnaopermitidos', visivel: false },
  nomemonitoramento: { exibir: 'main.template.nomemonitoramento', visivel: true },
  imagemmonitoramento: { exibir: 'main.template.imagemmonitoramento', visivel: false },
  tema: { exibir: 'main.template.tema', tipo: 'enum', entidade: 'template.tema' },
  avaliacaoia: { exibir: 'main.template.avaliacaoia', linhas: 10 },
};

const renderers = {
  'template.testebanda': TestebandaEnum,
  'template.testestatus': TestestatusEnum,
  'template.testequalidadestream': TestequalidadestreamEnum,
  'template.testeip': TesteipEnum,
  'template.exibicaoresultado': ExibicaoresultadoEnum,
  'template.tema': TemaEnum,
};

class TemplateFormulario extends Component {
  render() {
    const { model, contexto } = this.props;
    return (
      <TabViewComErro tentativaSalvar={contexto.state.tentativaSalvar} className="col-12 mt-2">
        <TabPanel header={UtilService.traduzir('main.template.aba.principal')} leftIcon="pi pi-home mr-2">
          <div className="grid w-full">
            <div className="field col-12 mt-3"><ModelTextInput model={model.descricao} contexto={contexto} /></div>
            <div className="field col-12 md:col-4 mt-3"><ModelDropDown model={model.testebanda} contexto={contexto} /></div>
            <div className="field col-12 md:col-4 mt-3"><ModelDropDown model={model.testestatus} contexto={contexto} /></div>
            <div className="field col-12 md:col-4 mt-3"><ModelDropDown model={model.testequalidadestream} contexto={contexto} /></div>
            <div className="field col-12 md:col-4 mt-3"><ModelDropDown model={model.testeip} contexto={contexto} /></div>
            <div className="field col-12 md:col-4 mt-3"><ModelTextInput model={model.testarping} contexto={contexto} /></div>
            <div className="field col-12 md:col-4 mt-3"><ModelDropDown model={model.exibicaoresultado} contexto={contexto} /></div>
            <div className="field col-12 md:col-4 mt-3"><ModelBoolean model={model.testepageopen} contexto={contexto} /></div>
            <div className="field col-12 md:col-4 mt-3"><ModelBoolean model={model.testepageresponse} contexto={contexto} /></div>
          </div>
        </TabPanel>
        <TabPanel header={UtilService.traduzir('main.template.aba.adicionais')} leftIcon="pi pi-cog mr-2">
          <div className="grid w-full">
            <div className="field col-12 md:col-6 mt-3"><ModelTextInput model={model.nomemonitoramento} contexto={contexto} /></div>
            <div className="field col-12 md:col-3 mt-3"><ModelFile model={model.imagemmonitoramento} contexto={contexto} /></div>
            <div className="field col-12 md:col-3 mt-3">
              <ModelDropDown
                model={model.tema}
                contexto={contexto}
                itemTemplateCustom={(opt) => <TemaOptionLabel chave={opt.id} />}
                valueTemplateCustom={(val) => <TemaOptionLabel chave={val?.id} />}
              />
            </div>
            <div className="field col-12 md:col-4 mt-3"><ModelBoolean model={model.exibirresultados} contexto={contexto} /></div>
            <div className="field col-12 md:col-4 mt-3"><ModelBoolean model={model.exibirclassificacao} contexto={contexto} /></div>
            <div className="field col-12 md:col-4 mt-3"><ModelBoolean model={model.permitirreinicio} contexto={contexto} /></div>
            <div className="field col-12 mt-3"><ModelTextInput model={model.identificadorterceiroscampo} contexto={contexto} /></div>
            <div className="field col-12 mt-3"><ModelTextInput model={model.blocospermitidos} contexto={contexto} /></div>
            <div className="field col-12 mt-3"><ModelTextArea model={model.mensagemblocosnaopermitidos} contexto={contexto} /></div>
          </div>
        </TabPanel>
        <TabPanel header={UtilService.traduzir('main.template.aba.avaliacaoia')} leftIcon="pi pi-sparkles mr-2">
          <div className="grid w-full">
            <div className="col-12 mt-3">
              <Message
                severity="info"
                className="w-full mb-3"
                content={(
                  <div className="p-2 line-height-3">
                    {UtilService.traduzir('main.template.avaliacaoia.explicacao')}
                  </div>
                )}
              />
            </div>
            <div className="field col-12 mt-3">
              <ModelTextArea model={model.avaliacaoia} contexto={contexto} autoResize />
            </div>
            <div className="col-12">
              <Button
                type="button"
                label={UtilService.traduzir('main.template.avaliacaoia.usarsugestao')}
                icon="pi pi-lightbulb"
                severity="secondary"
                outlined
                size="small"
                onClick={() => contexto.updateModelPath('avaliacaoia.value', SUGESTAO_PROMPT_AVALIACAOIA)}
              />
            </div>
          </div>
        </TabPanel>
      </TabViewComErro>
    );
  }
}

class TemplateTemplate extends Component {
  render() {
    return (
      <EntityGrid
        entidade={Template}
        model={model}
        identidade="main.template"
        permissoes={this.props.permissoes}
        icone="pi-sliders-h"
        storageKey="dt-templates"
        getRelacionados={(selected) => buildRelacionados('template', this.props.permissoes, this.props.navigate, () => selected?.[0])}
        renderers={renderers}
        configuracao={this.props.configuracao}
        navigate={this.props.navigate}
        location={this.props.location}
        searchparams={this.props.searchparams}
        formulario={(m, ctx) => <TemplateFormulario model={m} contexto={ctx} />}
      />
    );
  }
}

export default TemplateTemplate;
